import React, { useState } from 'react';
import { Input } from 'antd';
import { useQueryClient } from 'react-query';
import { Form, Formik } from 'formik';
import Modal from '../../../components/Modal';
import { useActions } from '../../../context/Actions.context';
import { Title } from '../../../components/styles';
import { MUTEAUTHOR, SOURCE, UNMUTEAUTHOR } from '../../../core/constants';
import showActionNotification from '../../../section/Common/utils/showActionNotification';
import { useUnmuteAuthor } from '../../../core/modules/book/hooks/useUnmuteAuthor';
import { useMuteAuthor } from '../../../core/modules/book/hooks/useMuteAuthor';
import { ReactComponent as UnmuteIcon } from '../../../assets/icons/bell-off-fill-unmute.svg';
import { ReactComponent as MuteIcon } from '../../../assets/icons/bell-off-outline-mute.svg';

const { TextArea } = Input;

interface IProps {
  selected_authors: string[];
  open: boolean;
  onClose: Function;
  onReset: Function;
  id: string;
}

const UnmuteSelectedAuthors: React.FC<IProps> = ({
  selected_authors,
  open,
  onClose,
  onReset,
  id
}) => {
  const {
    isActionLoading,
    setActionLoading,
    source: srcPath
  } = useActions();

  const { mutateAsync } = useUnmuteAuthor(UNMUTEAUTHOR);
  const { mutateAsync: muteAsync } = useMuteAuthor(MUTEAUTHOR);
  const queryClient = useQueryClient();
  const uniqueAuthors = Array.from(new Set(selected_authors));
  const [note, setNote] = useState('');

  const handleClose = (resetForm?: Function) => {
    setNote('');
    resetForm?.();
    onClose();
  };

  const onUnmuteAll = (_values, cb) => {
    if (isActionLoading) {
      return;
    }
    setActionLoading(true);

    try {
      const payload = {
        authors: uniqueAuthors,
        note
      };

      const capturedAuthors = [...uniqueAuthors];
      const promise = (mutateAsync as Function)(payload);

      cb.resetForm();

      promise.then(res => {
        if (res?.status === 200) {
          showActionNotification({
            type: 'success',
            message: 'Authors unmuted successfully',
            onUndo: async () => {
              await (muteAsync as Function)({
                authors: capturedAuthors,
                duration: { value: 6, unit: 'months' }
              });
            },
            queryClient,
            id
          });
        } else {
          showActionNotification({
            type: 'error',
            message: 'Failed to unmute authors'
          });
        }
      }).catch(() => {
        showActionNotification({
          type: 'error',
          message: 'Failed to unmute authors'
        });
      });

      promise.then(() => queryClient.refetchQueries(`get-${id}`));
      promise.then(() => queryClient.refetchQueries(`get-row-count${id}`));
      promise.then(() => handleClose(cb.resetForm));
      promise.finally(() => setActionLoading(false));
    } catch (error) {
      handleClose(cb.resetForm);
      onReset();

      showActionNotification({
        type: 'error',
        message: 'Failed to unmute selected authors'
      });

      setActionLoading(false);

      console.error('Unmute authors error', {
        selectedAuthors: uniqueAuthors,
        error
      });
    }
  };

  return (
    <Formik
      initialValues={{}}
      onSubmit={onUnmuteAll}
    >
      {({ handleSubmit, resetForm }) => (
        <Form>
          <Modal
            title='Unmute Selected Author(s)'
            visible={open}
            onOk={handleSubmit}
            okText='Unmute Authors'
            onCancel={() => handleClose(resetForm)}
            confirmLoading={isActionLoading}
            okButtonProps={{ disabled: isActionLoading }}
          >
            <Title>
              <p>
                Following <strong>{uniqueAuthors.length}</strong> author(s) will be unmuted {' '}
                <UnmuteIcon
                  className='actions-icons actions-icons-unmute'
                  style={{ paddingTop: '5px' }} />:
              </p>

              <br />

              <ul style={{ paddingLeft: 18 }}>
                {uniqueAuthors.slice(0, 5).map((a) => (
                  <li key={a}>{a}</li>
                ))}
                {uniqueAuthors.length > 5 && (
                  <li key='more'>+ {uniqueAuthors.length - 5} more...</li>
                )}
              </ul>

              <ul style={{ paddingLeft: 18, marginBottom: 16 }}>
                <li>When unmuted, these authors’ titles will be visible under
                  { srcPath['source'] === SOURCE.ao3 ? ' Popular ' : 'All New Titles or Hot Titles ' }
                  if their ranking criteria is met.
                </li>
                <li>
                  You can mute the author anytime again by clicking on {' '}
                  <MuteIcon
                    className='actions-icons actions-icons-mute'
                    style={{ paddingTop: '5px' }} /> Mute under Actions.
                </li>
              </ul>
            </Title>

            <TextArea
              placeholder='Leave a note stating the reason behind muting author(s)'
              maxLength={400}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              style={{ marginTop: 12 }}
            />

            <div style={{ textAlign: 'right', fontSize: 12, marginTop: 4 }}>
              {note.length} / 400
            </div>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default React.memo(UnmuteSelectedAuthors);
